import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Users, Clock, Dumbbell, ChevronRight, ArrowLeft, User } from 'lucide-react';
import { communityApi, CommunityMember } from '@/services/api';
import { Card, Button, Calendar, ImageViewer } from '@/components/common';
import type { Workout } from '@/types';
import { calculateAgeFromBirthDate } from '@/lib/age';

const Community = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedUserId = searchParams.get('user');
  const viewWorkoutId = searchParams.get('view');
  
  const [members, setMembers] = useState<CommunityMember[]>([]);
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [selectedWorkout, setSelectedWorkout] = useState<(Workout & { user: { id: string; name: string } }) | null>(null);
  const [selectedMember, setSelectedMember] = useState<CommunityMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [viewerImage, setViewerImage] = useState<{ src: string; alt: string } | null>(null);
  const [liveNow, setLiveNow] = useState(() => Date.now());

  const hasActiveWorkoutVisible = useMemo(
    () =>
      Boolean(viewWorkoutId && selectedWorkout && !selectedWorkout.endTime) ||
      workouts.some((w) => !w.endTime),
    [viewWorkoutId, selectedWorkout, workouts]
  );

  useEffect(() => {
    if (!hasActiveWorkoutVisible) return;
    const timer = setInterval(() => setLiveNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [hasActiveWorkoutVisible]);

  // Load members list
  useEffect(() => {
    const loadMembers = async () => {
      try {
        const response = await communityApi.getMembers();
        if (response.success && response.data) {
          setMembers(response.data.members);
        }
      } catch (error) {
        console.error('Failed to load members:', error);
      } finally {
        if (!selectedUserId && !viewWorkoutId) {
          setLoading(false);
        }
      }
    };
    loadMembers();
  }, []);

  // Load member's workouts when selected
  useEffect(() => {
    if (!selectedUserId) {
      setWorkouts([]);
      setSelectedMember(null);
      setCursor(null);
      setHasMore(true);
      if (!viewWorkoutId) setLoading(false);
      return;
    }

    const member = members.find(m => m.id === selectedUserId);
    setSelectedMember(member || null);
    loadWorkouts(selectedUserId, false);
  }, [selectedUserId, members]);

  // Load workout details when viewing
  useEffect(() => {
    if (!viewWorkoutId) {
      setSelectedWorkout(null);
      return;
    }

    const loadWorkoutDetails = async () => {
      setLoading(true);
      try {
        const response = await communityApi.getWorkoutDetails(viewWorkoutId);
        if (response.success && response.data) {
          setSelectedWorkout(response.data.workout);
        }
      } catch (error) {
        console.error('Failed to load workout details:', error);
      } finally {
        setLoading(false);
      }
    };
    loadWorkoutDetails();
  }, [viewWorkoutId]);

  useEffect(() => {
    if (!viewWorkoutId || !selectedWorkout || selectedWorkout.endTime) return;

    const refresh = async () => {
      try {
        const response = await communityApi.getWorkoutDetails(viewWorkoutId);
        if (response.success && response.data) {
          setSelectedWorkout(response.data.workout);
        }
      } catch (error) {
        console.error('Failed to refresh workout details:', error);
      }
    };

    const interval = setInterval(refresh, 20000);
    return () => clearInterval(interval);
  }, [viewWorkoutId, selectedWorkout?.id, selectedWorkout?.endTime]);

  const loadWorkouts = async (userId: string, loadMore = false) => {
    if (loadMore && !hasMore) return;

    setLoading(true);
    try {
      const response = await communityApi.getMemberWorkouts(userId, loadMore ? cursor || undefined : undefined);
      if (response.success && response.data) {
        if (loadMore) {
          setWorkouts((prev) => [...prev, ...response.data!.items]);
        } else {
          setWorkouts(response.data.items);
        }
        setCursor(response.data.nextCursor);
        setHasMore(!!response.data.nextCursor);
      }
    } catch (error) {
      console.error('Failed to load workouts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatLastActive = (lastActiveAt: string | null) => {
    if (!lastActiveAt) return 'Nincs adat';
    
    const now = new Date();
    const lastActive = new Date(lastActiveAt);
    const diffMs = now.getTime() - lastActive.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 5) return 'Aktív';
    if (diffMins < 60) return `${diffMins} perce`;
    if (diffHours < 24) return `${diffHours} órája`;
    if (diffDays === 1) return 'Tegnap';
    return `${diffDays} napja`;
  };

  const formatLastWorkout = (start: string | null, end: string | null) => {
    if (!start) return 'Még nem volt edzés';

    const date = new Date(start);
    const formatted = date.toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    if (!end) {
      return `${formatted} (folyamatban)`;
    }
    return formatted;
  };

  const formatDuration = useCallback((start: string, end: string | null) => {
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : new Date(liveNow);
    const durationMs = endDate.getTime() - startDate.getTime();
    const minutes = Math.floor(durationMs / 60000);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (!end) {
      if (hours > 0) {
        return `Folyamatban · ${hours} óra ${remainingMinutes} perc`;
      }
      return `Folyamatban · ${minutes} perc`;
    }

    if (hours > 0) {
      return `${hours} óra ${remainingMinutes} perc`;
    }
    return `${minutes} perc`;
  }, [liveNow]);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('hu-HU', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get dates with workouts for calendar highlighting
  const workoutDates = useMemo(() => {
    return workouts.map(w => new Date(w.startTime));
  }, [workouts]);

  // Filter workouts by selected date
  const filteredWorkouts = useMemo(() => {
    if (!selectedDate) return workouts;
    
    return workouts.filter(workout => {
      const workoutDate = new Date(workout.startTime);
      return (
        workoutDate.getFullYear() === selectedDate.getFullYear() &&
        workoutDate.getMonth() === selectedDate.getMonth() &&
        workoutDate.getDate() === selectedDate.getDate()
      );
    });
  }, [workouts, selectedDate]);

  // Workout details view
  if (viewWorkoutId && selectedWorkout) {
    return (
      <div>
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => setSearchParams({ user: selectedWorkout.user.id })}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Edzés részletei</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">{selectedWorkout.user.name} - {formatDate(selectedWorkout.startTime)}</p>
          </div>
        </div>

        {/* Workout summary */}
        <Card className="mb-6">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-lg ${selectedWorkout.endTime ? 'bg-green-100' : 'bg-yellow-100'}`}>
              <Dumbbell className={`w-6 h-6 ${selectedWorkout.endTime ? 'text-green-600' : 'text-yellow-600'}`} />
            </div>
            <div>
              {!selectedWorkout.endTime && (
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 mb-2 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-500" />
                  </span>
                  Élő edzés
                </span>
              )}
              <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-400">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {formatDuration(selectedWorkout.startTime, selectedWorkout.endTime)}
                </span>
                <span className="flex items-center gap-1">
                  <Dumbbell className="w-4 h-4" />
                  {selectedWorkout.exercises.length} gyakorlat
                </span>
                {selectedWorkout.bodyWeight && (
                  <span>Testsúly: {selectedWorkout.bodyWeight} kg</span>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Exercises */}
        <div className="space-y-4">
          {selectedWorkout.exercises.map((exercise) => (
            <Card key={exercise.id}>
              <div className="flex items-center gap-3 mb-4">
                {(exercise.bodyweightExercise?.photoUrl || exercise.machine?.photoUrl) ? (
                  <img
                    src={exercise.bodyweightExercise?.thumbnailUrl || exercise.machine?.thumbnailUrl || exercise.bodyweightExercise?.photoUrl || exercise.machine?.photoUrl || ''}
                    alt={exercise.bodyweightExercise?.name || exercise.machine?.name || ''}
                    className="w-12 h-12 rounded-lg object-cover cursor-pointer"
                    onClick={() => setViewerImage({
                      src: exercise.bodyweightExercise?.photoUrl || exercise.machine?.photoUrl || '',
                      alt: exercise.bodyweightExercise?.name || exercise.machine?.name || '',
                    })}
                  />
                ) : (
                  <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                    <Dumbbell className="w-6 h-6 text-gray-400" />
                  </div>
                )}
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">{exercise.bodyweightExercise?.name || exercise.machine?.name}</h3>
                  <p className="text-sm text-gray-500">{exercise.bodyweightExercise?.muscleGroup || exercise.machine?.type}</p>
                </div>
              </div>

              {exercise.sets.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-1 px-1 text-sm font-medium text-gray-600 w-8">#</th>
                        <th className="text-left py-1 px-1 text-sm font-medium text-gray-600 w-10"></th>
                        <th className="text-left py-1 px-1 text-sm font-medium text-gray-600">Részletek</th>
                        <th className="text-center py-1 px-1 text-sm font-medium text-gray-600 w-8"></th>
                        <th className="text-right py-1 px-1 text-sm font-medium text-gray-600 w-14">Idő</th>
                      </tr>
                    </thead>
                    <tbody>
                      {exercise.sets.map((set) => {
                        const setTypeLabel = set.setType === 'cardio' ? '🏃' : set.setType === 'dualHand' ? '🙌' : '💪';
                        const difficultyEmojis = ['', '😄', '🙂', '😐', '😓', '🥵'];
                        let detailsText = '';

                        if (set.setType === 'cardio' && set.durationSeconds) {
                          const mins = Math.floor(set.durationSeconds / 60);
                          const secs = set.durationSeconds % 60;
                          const levelText = set.resistanceLevel ? ` (fok: ${set.resistanceLevel})` : '';
                          detailsText = `${mins}:${secs.toString().padStart(2, '0')}${levelText}`;
                        } else if (set.setType === 'dualHand') {
                          detailsText = `B: ${set.leftReps || 0}×${set.leftWeight || 0}kg | J: ${set.rightReps || 0}×${set.rightWeight || 0}kg`;
                        } else {
                          detailsText = `${set.reps || 0} db × ${set.weight || 0} kg`;
                        }

                        return (
                          <tr key={set.id} className="border-b border-gray-100">
                            <td className="py-1 px-1 text-gray-500 text-sm">{set.setNumber}</td>
                            <td className="py-1 px-1">{setTypeLabel}</td>
                            <td className="py-1 px-1 text-sm">{detailsText}</td>
                            <td className="py-1 px-1 text-center text-lg" title={`Nehézség: ${set.difficulty}/5`}>
                              {difficultyEmojis[set.difficulty] || '😐'}
                            </td>
                            <td className="py-1 px-1 text-gray-600 dark:text-gray-400 text-sm text-right">
                              {formatTime(set.timestamp)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          ))}
        </div>

        <div className="mt-8 text-center">
          <Button
            variant="outline"
            icon={<ArrowLeft className="w-4 h-4" />}
            onClick={() => setSearchParams({ user: selectedWorkout.user.id })}
          >
            Vissza az edzésekhez
          </Button>
        </div>

        {/* Image Viewer Lightbox */}
        <ImageViewer
          src={viewerImage?.src || ''}
          alt={viewerImage?.alt || ''}
          isOpen={!!viewerImage}
          onClose={() => setViewerImage(null)}
        />
      </div>
    );
  }

  // Member's workouts view
  if (selectedUserId && selectedMember) {
    return (
      <div>
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => setSearchParams({})}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{selectedMember.name} edzései</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {(() => {
                const age = calculateAgeFromBirthDate(selectedMember.birthDate);
                return age !== null ? `${age} éves` : 'Kor nincs megadva';
              })()} • {selectedMember.workoutCount} edzés
            </p>
          </div>
        </div>

        {/* Calendar */}
        <div className="mb-6">
          <Calendar
            highlightedDates={workoutDates}
            selectedDate={selectedDate}
            onSelectDate={setSelectedDate}
          />
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredWorkouts.length === 0 ? (
          <Card>
            <div className="text-center py-8">
              <Dumbbell className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {selectedDate ? 'Nincs edzés ezen a napon' : 'Nincs még rögzített edzés'}
              </h3>
              {selectedDate && (
                <Button variant="outline" size="sm" onClick={() => setSelectedDate(null)}>
                  Szűrés törlése
                </Button>
              )}
            </div>
          </Card>
        ) : (
          <>
            <div className="space-y-4">
              {filteredWorkouts.map((workout) => (
                <Card key={workout.id}>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-start sm:items-center gap-3 min-w-0">
                      <div className={`p-2 sm:p-3 rounded-lg flex-shrink-0 ${workout.endTime ? 'bg-green-100' : 'bg-yellow-100'}`}>
                        <Dumbbell className={`w-5 h-5 sm:w-6 sm:h-6 ${workout.endTime ? 'text-green-600' : 'text-yellow-600'}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm sm:text-base">
                            {formatDate(workout.startTime)}
                          </h3>
                          {!workout.endTime && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                              <span className="relative flex h-1.5 w-1.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75" />
                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-yellow-500" />
                              </span>
                              Folyamatban
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                            {formatDuration(workout.startTime, workout.endTime)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Dumbbell className="w-3 h-3 sm:w-4 sm:h-4" />
                            {workout.exercises.length} gyakorlat
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setSearchParams({ user: selectedUserId!, view: workout.id })}
                      className="px-3 py-1.5 text-sm text-primary-600 hover:bg-primary-50 rounded-lg flex items-center gap-1 self-end sm:self-auto flex-shrink-0"
                    >
                      Részletek
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>

                  {workout.exercises.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex flex-wrap gap-2">
                        {workout.exercises.slice(0, 5).map((exercise) => (
                          <span
                            key={exercise.id}
                            className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm rounded"
                          >
                            {exercise.bodyweightExercise?.name || exercise.machine?.name}
                            <span className="text-gray-500 ml-1">
                              ({exercise.sets.length} sor)
                            </span>
                          </span>
                        ))}
                        {workout.exercises.length > 5 && (
                          <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm rounded">
                            +{workout.exercises.length - 5} további
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>

            {hasMore && (
              <div className="text-center mt-6">
                <Button variant="outline" onClick={() => loadWorkouts(selectedUserId, true)}>
                  Több betöltése
                </Button>
              </div>
            )}
          </>
        )}

        <div className="mt-8 text-center">
          <Button
            variant="outline"
            icon={<ArrowLeft className="w-4 h-4" />}
            onClick={() => setSearchParams({})}
          >
            Vissza a közösséghez
          </Button>
        </div>
      </div>
    );
  }

  // Members list view
  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Közösség</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1 sm:mt-2 text-sm sm:text-base">Nézd meg a többi tag edzéseit</p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : members.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Nincs még tag</h3>
            <p className="text-gray-600 dark:text-gray-400">Hamarosan csatlakoznak mások is!</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {members.map((member) => (
            <Card key={member.id}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 sm:p-3 rounded-lg flex-shrink-0 bg-primary-100">
                    <User className="w-5 h-5 sm:w-6 sm:h-6 text-primary-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm sm:text-base truncate">
                      {member.name}
                    </h3>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {(() => {
                        const age = calculateAgeFromBirthDate(member.birthDate);
                        return age !== null ? <span>{age} éves</span> : null;
                      })()}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                        {formatLastActive(member.lastActiveAt)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Dumbbell className="w-3 h-3 sm:w-4 sm:h-4" />
                        {member.workoutCount} edzés
                      </span>
                    </div>
                    <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1.5">
                      <span className="block">Utolsó edzés:</span>
                      <span className="block mt-0.5 text-gray-700 dark:text-gray-300">
                        {formatLastWorkout(member.lastWorkoutAt, member.lastWorkoutEndTime)}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setSearchParams({ user: member.id })}
                  className="px-3 py-1.5 text-sm text-primary-600 hover:bg-primary-50 rounded-lg flex items-center gap-1 flex-shrink-0"
                >
                  Edzések
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Community;
